import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowsClockwise, CheckCircle, PlugsConnected, Warning } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { EasyStayConnect } from "@/components/EasyStayConnect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PROPERTY_TYPES = ["Lodge", "Hotel", "Guest House", "Bed & Breakfast", "Apartments", "Motel", "Hostel", "Event Centre", "Conference Room", "Other"];

export default function Settings() {
  const { settings, refreshSettings } = useCurrency();
  const [form, setForm] = useState(null);
  const [sync, setSync] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);

  useEffect(() => {
    if (settings) setForm({ ...settings, rates: { ...settings.rates } });
  }, [settings]);

  useEffect(() => {
    api.get("/sync/overview").then(({ data }) => setSync(data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        property_name: form.property_name,
        property_type: form.property_type || "",
        address: form.address,
        rooms_count: parseInt(form.rooms_count, 10) || 0,
        phone: form.phone,
        vat_enabled: form.vat_enabled,
        vat_rate: parseFloat(form.vat_rate),
        rates: { USD: parseFloat(form.rates.USD), CNY: parseFloat(form.rates.CNY) },
        easystay_property_id: form.easystay_property_id || "",
        easystay_property_name: form.easystay_property_name || "",
        easystay_property_address: form.easystay_property_address || "",
        auto_relist_all: !!form.auto_relist_all,
        onboarding_completed: true, // reaching Settings implies onboarding is done
      });
      await refreshSettings();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const { data } = await api.post("/sync/push-now");
      if (data.status === "ok") toast.success("Availability pushed to EasyStay");
      else toast.info(`Push ${data.status}${data.detail ? `: ${data.detail}` : ""}`);
      const { data: s } = await api.get("/sync/overview");
      setSync(s);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPushing(false);
    }
  };

  if (!form) return <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">Property details, tax and exchange rates.</p>
      </div>

      <form onSubmit={save} className="space-y-6">
        <div className="border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-bold">Property</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Property name</Label>
              <Input data-testid="settings-property-name" value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="settings-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+260 …" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Address</Label>
              <Input data-testid="settings-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Property type</Label>
              <Select value={form.property_type || ""} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger data-testid="settings-property-type"><SelectValue placeholder="Choose a type…" /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Number of rooms</Label>
              <Input type="number" min="0" data-testid="settings-rooms-count" value={form.rooms_count ?? ""} onChange={(e) => setForm({ ...form, rooms_count: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>EasyStay listing</Label>
              {form.easystay_property_id ? (
                <div className="flex items-center justify-between gap-3 border border-border bg-background px-3 py-2.5" data-testid="settings-easystay-connected">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <PlugsConnected size={18} className="shrink-0 text-status-confirmed" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{form.easystay_property_name || "Connected listing"}</div>
                      <div className="truncate text-xs text-muted-foreground">{form.easystay_property_address || form.easystay_property_id}</div>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setConnectOpen(true)} data-testid="settings-easystay-change">Change</Button>
                    <Button type="button" size="sm" variant="ghost" data-testid="settings-easystay-disconnect"
                      onClick={() => { setForm({ ...form, easystay_property_id: "", easystay_property_name: "", easystay_property_address: "" }); toast.info("Disconnected — save settings to apply"); }}>
                      Disconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setConnectOpen(true)} data-testid="settings-easystay-connect">
                    Connect to EasyStay
                  </Button>
                  <p className="mt-1 text-[11px] text-muted-foreground">Optional — links this workspace to your listing on easystayzm.com for automatic relisting and two-way sync. No IDs to copy: just search and pick.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-bold">Tax (VAT)</h2>
          <div className="mt-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Charge VAT on invoices</div>
              <div className="text-xs text-muted-foreground">Zambian standard rate is 16%.</div>
            </div>
            <Switch checked={form.vat_enabled} onCheckedChange={(v) => setForm({ ...form, vat_enabled: v })} data-testid="settings-vat-toggle" />
          </div>
          {form.vat_enabled && (
            <div className="mt-4 w-40 space-y-1.5">
              <Label>VAT rate (%)</Label>
              <Input type="number" min="0" max="100" step="0.1" data-testid="settings-vat-rate" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} />
            </div>
          )}
        </div>

        <div className="border border-border bg-card p-6">
          <h2 className="font-heading text-lg font-bold">Exchange rates</h2>
          <p className="mt-1 text-xs text-muted-foreground">Base currency is ZMW (K). Rates below mean: 1 ZMW = X units of the currency.</p>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>1 ZMW = USD</Label>
              <Input type="number" min="0.0001" step="0.0001" data-testid="settings-rate-usd" value={form.rates.USD} onChange={(e) => setForm({ ...form, rates: { ...form.rates, USD: e.target.value } })} />
            </div>
            <div className="space-y-1.5">
              <Label>1 ZMW = CNY</Label>
              <Input type="number" min="0.0001" step="0.0001" data-testid="settings-rate-cny" value={form.rates.CNY} onChange={(e) => setForm({ ...form, rates: { ...form.rates, CNY: e.target.value } })} />
            </div>
          </div>
        </div>

        <div className="border border-border bg-card p-6" data-testid="listings-section">
          <h2 className="font-heading text-lg font-bold">EasyStay listings</h2>
          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium">Auto-relist all rooms when vacant</div>
              <div className="text-xs text-muted-foreground">
                Automatically republish every room on EasyStay when a booking ends or is cancelled — no per-room setup needed.
                When off, only rooms with their own auto-relist switch republish automatically; the rest appear in Vacant Properties for manual relisting.
              </div>
            </div>
            <Switch checked={!!form.auto_relist_all} onCheckedChange={(v) => setForm({ ...form, auto_relist_all: v })} data-testid="settings-auto-relist-all-toggle" />
          </div>
        </div>

        <Button type="submit" disabled={saving} data-testid="settings-save" className="transition-colors">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </form>

      <div className="border border-border bg-card p-6" data-testid="sync-section">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">EasyStay platform sync</h2>
          {sync?.connected ? (
            <span className="flex items-center gap-1.5 rounded-sm border border-status-confirmed/40 bg-status-confirmed/10 px-2 py-1 text-xs font-medium text-status-confirmed" data-testid="sync-connection-status">
              <ArrowsClockwise size={13} /> Connected · live
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-sm border border-status-pending/40 bg-status-pending/10 px-2 py-1 text-xs font-medium text-status-pending" data-testid="sync-connection-status">
              <ArrowsClockwise size={13} /> Ready — set property ID
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{sync?.message}</p>
        {(() => {
          const lastOut = sync?.last_events?.find((e) => e.direction === "outbound");
          if (!lastOut || (lastOut.status !== "error" && lastOut.status !== "pending")) return null;
          return (
            <div
              className={`mt-3 flex items-start gap-2 border px-3 py-2.5 text-xs ${lastOut.status === "error" ? "border-destructive/40 bg-destructive/10 text-destructive" : "border-status-pending/40 bg-status-pending/10 text-status-pending"}`}
              data-testid="sync-error-banner"
              role="alert"
            >
              <Warning size={15} className="mt-0.5 shrink-0" />
              <span>
                <span className="font-semibold">Last EasyStay sync {lastOut.status === "error" ? "failed" : "didn't complete"}:</span>{" "}
                {lastOut.event} — {lastOut.detail}. Check the EasyStay listing connection above and the setup contract below.
              </span>
            </div>
          );
        })()}
        {sync && (
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div className="border border-border p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Bookings synced</div>
              <div className="font-heading text-xl font-bold" data-testid="sync-bookings-count">{sync.bookings.synced} / {sync.bookings.total}</div>
            </div>
            <div className="border border-border p-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Rooms synced</div>
              <div className="font-heading text-xl font-bold" data-testid="sync-rooms-count">{sync.rooms.synced} / {sync.rooms.total}</div>
            </div>
          </div>
        )}
        <div className="mt-4">
          <Button size="sm" onClick={pushNow} disabled={pushing} data-testid="sync-push-now" className="transition-colors">
            <ArrowsClockwise size={14} className={`mr-1.5 ${pushing ? "animate-spin" : ""}`} />
            {pushing ? "Pushing…" : "Push availability now"}
          </Button>
        </div>
        {sync?.last_events?.length > 0 && (
          <div className="mt-4 border-t border-border pt-3" data-testid="sync-events">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recent sync activity</div>
            <div className="mt-2 space-y-1.5">
              {sync.last_events.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 text-xs" data-testid={`sync-event-${e.id}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className={`shrink-0 font-semibold ${e.status === "ok" ? "text-status-confirmed" : e.status === "error" ? "text-destructive" : "text-status-pending"}`}>
                      {e.status}
                    </span>
                    <span className="shrink-0 text-muted-foreground">{e.direction} · {e.event}</span>
                    <span className="truncate">{e.detail}</span>
                  </span>
                  <span className="shrink-0 text-muted-foreground">{format(new Date(e.at), "dd MMM HH:mm")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground" data-testid="sync-integration-info">
          <div className="flex items-center gap-2 font-semibold text-foreground">
            <CheckCircle size={14} className="text-status-confirmed" /> EasyStay-side setup contract
          </div>          <ul className="mt-1.5 list-inside list-disc space-y-1">
            <li>Manager pushes availability to <code>{sync?.base_url || "https://easystayzm.com"}/api/sync/manager/availability</code> (header <code>X-Sync-Key</code>) on every room/booking change.</li>
            <li>Manager upserts each room (create/update) to <code>POST /api/sync/manager/rooms</code> — stable <code>rm_*</code> room_external_id + title + price_per_night_zmw + host_email; a room is marked listed only when the response confirms <code>created</code> / <code>visible_to_guests</code>.</li>
            <li>EasyStay sends guest bookings to <code>POST /api/sync/easystay/events</code> (header <code>X-Sync-Key</code>) — contactless bookings get a digital key back in the response for SMS delivery.</li>
            <li>EasyStay can pull availability anytime: <code>GET /api/sync/easystay/availability?property_id=…</code></li>
            <li>Host dashboard deep link ("Manage in EasyStay Manager" button): from easystayzm.com's own JS, first-party fetch <code>/api/auth/handoff?client=manager</code> → read <code>session_token</code> → navigate to <code>{window.location.origin}/login#eso_token=&lt;session_token&gt;</code>. Works in every browser — no third-party cookies or CORS involved. (Fallback link <code>/login?sso=easystay</code> relies on the browser sending the EasyStay cookie cross-origin, which Safari/Firefox block.)</li>
          </ul>
        </div>
      </div>

      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent data-testid="easystay-connect-dialog">
          <DialogHeader>
            <DialogTitle>Connect to EasyStay</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Find your listing on easystayzm.com — the listing ID is stored automatically when you pick one.
          </p>
          <EasyStayConnect
            selectedId={form.easystay_property_id}
            onSelect={(p) => {
              setForm({
                ...form,
                easystay_property_id: p.id,
                easystay_property_name: p.name,
                easystay_property_address: [p.address, p.city].filter(Boolean).join(", "),
              });
              setConnectOpen(false);
              toast.success(`Selected "${p.name}" — save settings to apply`);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
